import React, { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw, SkipForward } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout, ConfirmModal, FormField, Input, Separator,
  Textarea,
} from "../../ui";
import { LIMITS, formatDue, marksProblem, parseNumber, percentOf } from "./assignmentRules";

/**
 * GradePanel — marks and feedback for one submission, and the other way to
 * answer it: send it back for changes.
 *
 * It owns the form only. Saving is the page's (`onGrade` / `onReturn` return a
 * promise that rejects with the server's error), so the panel can keep what the
 * grader typed when a save is refused.
 *
 * Ctrl/⌘ + Enter saves.
 *
 * Props:
 *   submission, maxMarks
 *   onGrade({ marks, feedback, next }) · onReturn({ feedback })
 *   hasNext   — offer "Save & next"
 *   onDirtyChange(boolean)
 *   busy
 */
const GradePanel = ({ submission, maxMarks, onGrade, onReturn, hasNext, onDirtyChange, busy }) => {
  const initialMarks = submission.marks == null ? "" : String(submission.marks);
  const initialFeedback = submission.status === "graded" ? submission.feedback || "" : "";
  const [marks, setMarks] = useState(initialMarks);
  const [feedback, setFeedback] = useState(initialFeedback);
  const [errors, setErrors] = useState({});
  const [confirmReturn, setConfirmReturn] = useState(false);

  // A different submission is a fresh form.
  useEffect(() => {
    setMarks(initialMarks);
    setFeedback(initialFeedback);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission._id, submission.submittedAt, submission.status]);

  const dirty = marks !== initialMarks || feedback !== initialFeedback;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const value = parseNumber(marks);
  const percent = percentOf(value, maxMarks);

  const grade = async (next = false) => {
    const problem = marksProblem(value, maxMarks);
    if (problem) {
      setErrors({ marks: problem });
      return;
    }
    setErrors({});
    try {
      await onGrade({ marks: value, feedback: feedback.trim(), next });
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) setErrors(fieldErrors);
    }
  };

  const sendBack = async () => {
    if (!feedback.trim()) {
      setErrors({ feedback: "Tell the student what to change." });
      return;
    }
    if (submission.status === "graded" && !confirmReturn) {
      setConfirmReturn(true);
      return;
    }
    setConfirmReturn(false);
    setErrors({});
    try {
      await onReturn({ feedback: feedback.trim() });
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) setErrors(fieldErrors);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !busy) {
      e.preventDefault();
      grade(false);
    }
  };

  return (
    <AppCard as="section" aria-labelledby="grade-panel-title">
      <AppCardHeader>
        <AppCardTitle id="grade-panel-title">Grade</AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        {/* A form so Enter in the marks box saves, as anyone typing a number expects. */}
        <form
          noValidate
          onKeyDown={onKeyDown}
          onSubmit={(e) => { e.preventDefault(); grade(false); }}
          className="tw-flex tw-flex-col tw-gap-4"
        >
          {submission.status === "graded" && (
            <Callout tone="success">
              Graded {submission.marks} / {maxMarks}
              {submission.gradedBy?.name ? ` by ${submission.gradedBy.name}` : ""}
              {submission.gradedAt ? ` on ${formatDue(submission.gradedAt)}` : ""}. Saving again updates it.
            </Callout>
          )}
          {submission.status === "resubmit_requested" && (
            <Callout tone="info">
              Sent back{submission.lastReturn?.at ? ` on ${formatDue(submission.lastReturn.at)}` : ""} — waiting for the student to resubmit.
              You can still grade this version.
            </Callout>
          )}

          <FormField
            label="Marks"
            required
            error={errors.marks}
            hint={percent != null ? `${percent}% of ${maxMarks}` : `Out of ${maxMarks}`}
          >
            <Input
              type="number"
              inputMode="decimal"
              size="lg"
              min={0}
              max={maxMarks}
              step="any"
              value={marks}
              onChange={(e) => {
                setMarks(e.target.value);
                if (errors.marks) setErrors((x) => ({ ...x, marks: "" }));
              }}
              placeholder="0"
              trailing={<span className="tw-text-[0.8125rem] tw-text-content-subtle">/ {maxMarks}</span>}
              trailingPad="text"
              disabled={busy}
            />
          </FormField>
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            <AppButton type="button" size="xs" variant="outline" disabled={busy} onClick={() => setMarks(String(maxMarks))}>
              Full marks
            </AppButton>
            <AppButton type="button" size="xs" variant="outline" disabled={busy} onClick={() => setMarks("0")}>
              Zero
            </AppButton>
          </div>

          <FormField
            label="Feedback for the student"
            error={errors.feedback}
            hint={`They read this with their marks. ${feedback.length.toLocaleString("en-IN")} / ${LIMITS.feedback.toLocaleString("en-IN")}`}
          >
            <Textarea
              rows={5}
              value={feedback}
              onChange={(e) => {
                setFeedback(e.target.value);
                if (errors.feedback) setErrors((x) => ({ ...x, feedback: "" }));
              }}
              maxLength={LIMITS.feedback}
              placeholder="What was good, and what to improve."
              disabled={busy}
            />
          </FormField>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton type="submit" disabled={busy}>
              <CheckCircle2 size={16} /> {busy ? "Saving…" : submission.status === "graded" ? "Update grade" : "Save grade"}
            </AppButton>
            {hasNext && (
              <AppButton type="button" variant="outline" disabled={busy} onClick={() => grade(true)}>
                <SkipForward size={16} /> Save &amp; next
              </AppButton>
            )}
          </div>
          <p className="tw-m-0 tw-text-xs tw-text-content-subtle">Ctrl + Enter saves.</p>

          <Separator />

          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
              Not ready to grade? Send it back — your feedback above tells the student what to change, and they can resubmit even after the due date.
            </p>
            <div>
              <AppButton
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || submission.status === "resubmit_requested"}
                onClick={sendBack}
              >
                <RotateCcw size={16} /> Ask for changes
              </AppButton>
            </div>
          </div>
        </form>
      </AppCardContent>

      <ConfirmModal
        show={confirmReturn}
        title="Send graded work back?"
        message={`This removes the grade of ${submission.marks} / ${maxMarks} and asks the student to resubmit.`}
        confirmText="Send back"
        variant="warning"
        loading={busy}
        onConfirm={sendBack}
        onHide={() => setConfirmReturn(false)}
      />
    </AppCard>
  );
};

export default GradePanel;
